/* ============================================================
   SINGSPACE ADMIN - HOOK GESTION ESPACES
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/api';

export interface SpaceListItem {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string;
  shortDesc?: string | null;
  imageUrl?: string | null;
  workflow: string;
  isActive: boolean;
  isPublic: boolean;
  order: number;
  colorPrimary: string;
  colorAccent: string;
  createdAt: string;
  updatedAt: string;
  allowMultiDay?: boolean;
  allowHalfDay?: boolean;
  allowFullDay?: boolean;
  _count?: {
    capacityBrackets: number;
    equipments: number;
    roomLayouts: number;
    pricingGroups: number;
  };
}

export function useAdminSpaces() {
  const [spaces, setSpaces] = useState<SpaceListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  const [page, setPage] = useState(1);

  const fetchSpaces = useCallback(async (params?: Record<string, unknown>) => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await apiService.getSpaces(params);
      setSpaces(res.data.data);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des espaces';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpaces({ page, limit: 50 });
  }, [fetchSpaces, page]);

  const toggleSpace = useCallback(
    async (id: string, field: string) => {
      const previous = spaces.find((s) => s.id === id);
      if (!previous) return;

      setSpaces((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: !s[field as keyof SpaceListItem] } : s)));

      try {
        await apiService.toggleSpace(id, field);
      } catch (err) {
        console.error(`[Spaces] toggleSpace — ❌ rollback id=${id} field=${field}`);
        setSpaces((prev) =>
          prev.map((s) => (s.id === id ? { ...s, [field]: previous[field as keyof SpaceListItem] } : s)),
        );
        throw err;
      }
    },
    [spaces],
  );

  const deleteSpace = useCallback(
    async (id: string) => {
      const previous = spaces.find((s) => s.id === id);

      setSpaces((prev) => prev.filter((s) => s.id !== id));

      try {
        await apiService.deleteSpace(id);
      } catch (err) {
        if (previous) {
          setSpaces((prev) => {
            const restored = [...prev];
            restored.splice(previous.order || 0, 0, previous);
            restored.sort((a, b) => (a.order || 0) - (b.order || 0));
            return restored;
          });
        }
        throw err;
      }
    },
    [spaces],
  );

  const duplicateSpace = useCallback(async (id: string) => {
    const res = await apiService.duplicateSpace(id);
    const newSpace = res.data.data as SpaceListItem;
    setSpaces((prev) => [...prev, newSpace]);
    return newSpace;
  }, []);

  return {
    spaces,
    isLoading,
    error,
    pagination,
    page,
    setPage,
    refetch: fetchSpaces,
    toggleSpace,
    deleteSpace,
    duplicateSpace,
  };
}
